import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// Services
import { environment } from '../../../services/global-environment';
import { I18nService } from '../../../services/i18n.service';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { AzureBlobService } from '../../../services/azure-blob.service';

// Interfaces
import { MobileChemicalDetailResponse, MobileStockOperationRequest } from '../../../../database_model/database_model';

@Component({
  selector: 'app-chemical-mobile-view',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe],
  templateUrl: './chemical-mobile-view.component.html',
  styleUrl: './chemical-mobile-view.component.scss'
})
export class ChemicalMobileViewComponent implements OnInit, OnDestroy {
  // 路由參數
  inventoryId: string = '';

  // 資料狀態
  loading: boolean = true;
  loadingOperation: boolean = false;
  inventoryData: any = null;
  sdsData: any = null;
  error: string = '';
  successMessage: string = '';

  // PDF 查看相關
  pdfUrl: string = '';
  safePdfUrl: SafeResourceUrl | null = null;
  showPdfViewer: boolean = false;

  // 操作表單
  operationType: 'in' | 'out' | 'update' = 'in';
  quantity: number = 0;
  note: string = '';
  showOperationForm: boolean = false;

  // Unsubscribe
  private destroy$ = new Subject<void>();

  // API URL
  private apiUrl = environment.apiUrl;

  constructor(
    private route: ActivatedRoute,
    private http: HttpClient,
    private i18nService: I18nService,
    private azureBlobService: AzureBlobService,
    private sanitizer: DomSanitizer
  ) {}

  // 多國語系便捷方法
  private get translate() {
    return this.i18nService;
  }

  ngOnInit(): void {
    // 從路由取得庫存 ID
    this.inventoryId = this.route.snapshot.paramMap.get('id') || '';

    if (!this.inventoryId) {
      this.error = this.translate.translate('chemical.mobile.invalid_id');
      this.loading = false;
      return;
    }

    // 載入化學品資料
    this.loadChemicalData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * 載入化學品資料（使用真實 API）
   */
  async loadChemicalData(): Promise<void> {
    this.loading = true;
    this.error = '';
    try {
      // 呼叫真實 API
      const response = await this.http.get<any>(
        `${this.apiUrl}/api/chemical-inventory/detail/${this.inventoryId}`
      ).toPromise();

      if (response && response.success) {
        this.inventoryData = response.data.inventory;
        this.sdsData = response.data.sds;
      } else {
        throw new Error(response?.message || '載入失敗');
      }
    } catch (error: any) {
      this.error = this.translate.translate('chemical.mobile.load_failed');

      // Fallback 到 Mock 資料（開發用）
      if (error.status === 0 || error.status === 404) {
        await this.loadMockData();
      }
    } finally {
      this.loading = false;
    }
  }

  /**
   * 載入 Mock 資料（Phase 1 開發用）
   */
  private async loadMockData(): Promise<void> {
    // 模擬 API 延遲
    await new Promise(resolve => setTimeout(resolve, 500));

    // Mock 庫存資料
    this.inventoryData = {
      id: this.inventoryId,
      companyid: environment.companyid,
      departmentId: 'dept-001',
      departmentName: '研發部',
      chemicalId: 'chem-001',
      chemicalName: '丙酮',
      currentStock: 150,
      unit: '公升',
      enableAlerts: true,
      maxThreshold: 200,
      minThreshold: 20,
      lastUpdated: new Date().toISOString()
    };

    // Mock SDS 資料
    this.sdsData = {
      id: 'sds-001',
      chemicalId: 'chem-001',
      chineseName: '丙酮',
      englishName: 'Acetone',
      dangerLevel: 3,
      ghsPictograms: ['GHS02', 'GHS07'],
      pdfUrl: 'https://example.com/sds/acetone.pdf' // Mock PDF URL
    };
  }

  /**
   * 開啟操作表單
   */
  openOperationForm(type: 'in' | 'out' | 'update'): void {
    this.operationType = type;
    this.quantity = 0;
    this.note = '';
    this.showOperationForm = true;
    this.successMessage = '';
    this.error = '';
  }

  /**
   * 關閉操作表單
   */
  closeOperationForm(): void {
    this.showOperationForm = false;
    this.quantity = 0;
    this.note = '';
  }

  /**
   * 執行庫存操作（使用真實 API）
   */
  async performStockOperation(): Promise<void> {
    // 驗證輸入
    if (!this.validateOperation()) {
      return;
    }

    this.loadingOperation = true;
    this.error = '';
    this.successMessage = '';

    try {
      // 呼叫真實 API
      const payload = {
        inventoryId: this.inventoryId,
        operationType: this.operationType,
        quantity: this.quantity,
        note: this.note,
        recordDate: new Date().toISOString()
      };

      const response = await this.http.post<any>(
        `${this.apiUrl}/api/chemical-inventory/mobile/operation`,
        payload
      ).toPromise();

      if (response && response.success) {
        // 更新本地資料
        this.inventoryData.currentStock = response.data.newStock;
        this.inventoryData.lastUpdated = new Date().toISOString();

        // 顯示成功訊息
        this.successMessage = this.translate.translate('chemical.mobile.operation_success');

        // 關閉表單
        this.closeOperationForm();
      } else {
        throw new Error(response?.message || '操作失敗');
      }
    } catch (error: any) {
      // 特殊錯誤處理
      if (error.error && error.error.error === 'INSUFFICIENT_STOCK') {
        this.error = this.translate.translate('chemical.mobile.insufficient_stock');
      } else {
        this.error = this.translate.translate('chemical.mobile.operation_failed');
      }

      // Fallback 到 Mock 操作（開發用）
      if (error.status === 0) {
        await this.performMockOperation();
        this.closeOperationForm();
      }
    } finally {
      this.loadingOperation = false;
    }
  }

  /**
   * 模擬操作（Phase 1 開發用）
   */
  private async performMockOperation(): Promise<void> {
    // 模擬 API 延遲
    await new Promise(resolve => setTimeout(resolve, 800));

    // 計算新的庫存數量
    let newStock = this.inventoryData.currentStock;

    switch (this.operationType) {
      case 'in':
        newStock += this.quantity;
        break;
      case 'out':
        if (this.inventoryData.currentStock < this.quantity) {
          throw new Error(this.translate.translate('chemical.mobile.insufficient_stock'));
        }
        newStock -= this.quantity;
        break;
      case 'update':
        newStock = this.quantity;
        break;
    }

    // 更新本地資料
    this.inventoryData.currentStock = newStock;
    this.inventoryData.lastUpdated = new Date().toISOString();

    // 顯示成功訊息
    this.successMessage = this.translate.translate('chemical.mobile.operation_success');
  }

  /**
   * 驗證操作輸入
   */
  private validateOperation(): boolean {
    if (this.quantity <= 0) {
      this.error = this.translate.translate('chemical.mobile.invalid_quantity');
      return false;
    }

    if (this.operationType === 'out' && this.quantity > this.inventoryData.currentStock) {
      this.error = this.translate.translate('chemical.mobile.insufficient_stock');
      return false;
    }

    return true;
  }

  /**
   * 查看 SDS PDF
   */
  viewPdf(): void {
    if (!this.sdsData || !this.sdsData.pdfUrl) {
      this.error = this.translate.translate('chemical.mobile.no_sds');
      return;
    }

    this.pdfUrl = this.sdsData.pdfUrl;
    // Sanitize URL using DomSanitizer to bypass security restrictions for iframe display
    this.safePdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.pdfUrl);
    this.showPdfViewer = true;
  }

  /**
   * 關閉 PDF 查看器
   */
  closePdfViewer(): void {
    this.showPdfViewer = false;
    this.safePdfUrl = null;
  }

  /**
   * 下載 SDS PDF
   */
  downloadPdf(): void {
    if (!this.sdsData || !this.sdsData.pdfUrl) {
      this.error = this.translate.translate('chemical.mobile.no_sds');
      return;
    }

    // 開啟下載連結
    window.open(this.sdsData.pdfUrl, '_blank');
  }

  /**
   * 取得危險等級樣式 class
   */
  getDangerLevelClass(): string {
    if (!this.sdsData) return '';

    switch (this.sdsData.dangerLevel) {
      case 1: return 'danger-low';
      case 2: return 'danger-medium';
      case 3: return 'danger-high';
      default: return '';
    }
  }

  /**
   * 取得危險等級文字
   */
  getDangerLevelText(): string {
    if (!this.sdsData) return '';

    switch (this.sdsData.dangerLevel) {
      case 1: return this.translate.translate('chemical.danger_level.low');
      case 2: return this.translate.translate('chemical.danger_level.medium');
      case 3: return this.translate.translate('chemical.danger_level.high');
      default: return this.translate.translate('common.unknown');
    }
  }

  /**
   * 取得庫存狀態 class
   */
  getStockStatusClass(): string {
    if (!this.inventoryData || !this.inventoryData.enableAlerts) {
      return 'stock-normal';
    }

    const stock = this.inventoryData.currentStock;

    if (stock <= 0) {
      return 'stock-empty';
    }

    if (this.inventoryData.minThreshold && stock < this.inventoryData.minThreshold) {
      return 'stock-warning';
    }

    if (this.inventoryData.maxThreshold && stock > this.inventoryData.maxThreshold) {
      return 'stock-warning';
    }

    return 'stock-normal';
  }

  /**
   * 取得操作類型標題
   */
  getOperationTitle(): string {
    switch (this.operationType) {
      case 'in': return this.translate.translate('chemical.mobile.operation.in');
      case 'out': return this.translate.translate('chemical.mobile.operation.out');
      case 'update': return this.translate.translate('chemical.mobile.operation.update');
      default: return '';
    }
  }
}
